import { computed, makeObservable, observable } from 'mobx';
import { Services } from '@project/API';
import { MapProperties } from '@datum-map';
import { UserLayer } from './UserLayers/UserLayer';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { IUserLayerRecordProperties } from '../API/resources/userLayer';

export class UserLayerLoader {
  private _services: Services;
  private _mapProperties: MapProperties;
  @observable private _isLoading = false;
  @observable layers: Map<string | number, UserLayer> = new Map();

  constructor(services: Services, mapProperties: MapProperties) {
    this._services = services;
    this._mapProperties = mapProperties;

    makeObservable<UserLayerLoader>(this);
  }

  @computed get isLoading() {
    return this._isLoading;
  }

  isUserLayerFeature(feature: Feature<Point>): boolean {
    return [...this.layers.values()].some((l) => {
      return (
        l.hasFeature(feature) &&
        this._isUserLayerProperties(feature.getProperties())
      );
    });
  }

  getLayerIdByFeature(feature: Feature<Point>) {
    const layer = Array.from(this.layers).find(([_, item]) =>
      item.hasFeature(feature),
    );

    if (layer && layer[1]) {
      return layer[1].id;
    } else {
      return null;
    }
  }

  showLayer = async (
    layerItemId: number,
    onError: (massage: string) => void,
    filterParams?: URLSearchParams,
  ) => {
    this._isLoading = true;

    try {
      const result = await this._services.userLayerGeoJSONLayer.get({
        projection: this._mapProperties.map.getView().getProjection().getCode(),
        params: filterParams
          ? new URLSearchParams([...filterParams, ['_page_size', '0']])
          : new URLSearchParams({
              is_checked: 'true',
              is_archived: 'false',
              category: layerItemId.toString(),
              _page_size: '0',
            }),
      });

      const layer = new UserLayer(this._mapProperties.map, layerItemId);

      layer.addFeatures(result);

      if (this.layers.has(layerItemId)) {
        this.removeLayer(layerItemId);
      }

      layer.show();

      this.layers.set(layerItemId, layer);
    } catch (error) {
      console.error(error);
      onError('Не удалось загрузить слой');
    }
    this._isLoading = false;
  };

  isLayerVisible = (layerId: number) => {
    return this.layers.has(layerId);
  };

  removeLayer = (layerId: number) => {
    const layer = this.layers.get(layerId);

    if (!layer) return;

    layer.hide();
    this.layers.delete(layerId);
  };

  private _isUserLayerProperties(
    value: unknown,
  ): value is IUserLayerRecordProperties {
    if (typeof value !== 'object' || value === null) return false;

    const obj = value as Record<string, unknown>;

    return (
      typeof obj['title'] === 'string' &&
      typeof obj['description'] === 'string' &&
      typeof obj['category'] === 'number' &&
      typeof obj['is_checked'] === 'boolean' &&
      typeof obj['is_archived'] === 'boolean' &&
      typeof obj['created'] === 'string' &&
      typeof obj['date_published'] === 'string' &&
      typeof obj['author'] === 'string' &&
      typeof obj['user_id'] === 'number' &&
      Array.isArray(obj['photo'])
    );
  }
}
